/**
 * The guide — the second branch of Småken. Where to go in each town, what we
 * actually think of the places, and the products we'd put money on ourselves.
 */

import { PLACES, Place } from './smaken';

export interface Verdict {
  slug: string;
  /** Our score out of 5, in halves. This is an opinion, not an average. */
  rating: number;
  /** One line that earns the score. */
  verdict: string;
  /** The single thing to order when you get there. */
  order: string;
}

export const VERDICTS: Verdict[] = [
  {
    slug: 'lilla-verket',
    rating: 4.5,
    verdict: 'Få saker på menyn, alla gjorda ordentligt. Det är svårare än det låter.',
    order: 'Croissanten, om du kommer före tio på lördagen.',
  },
  {
    slug: 'top-notch-bakery',
    rating: 4,
    verdict: 'Ett konditori som vågar testa. Ibland spretigt, oftast briljant.',
    order: 'Veckans specialbulle. Fråga vad den är innan du bestämmer dig.',
  },
  {
    slug: 'askelyckan',
    rating: 4.5,
    verdict: 'Gamla skolan utan att vara museum. Prinsesstårtan är referensen.',
    order: 'Budapesttårtan. Trettio år av samma recept av en anledning.',
  },
  {
    slug: 'kok-11',
    rating: 5,
    verdict: 'Säsongsmat utan predikan. De bästa råvarorna i länet hamnar här.',
    order: 'Vad kocken än rekommenderar. De har rätt.',
  },
  {
    slug: 'pm-och-vanner',
    rating: 4.5,
    verdict: 'Finmat som inte känns stel. Servisen kan sin sak utan att vara på.',
    order: 'Avsmakningsmenyn med dryckespaket, om ni har kvällen.',
  },
  {
    slug: 'sorgardens-mejeri',
    rating: 4,
    verdict: 'Ost som smakar av var den kommer ifrån. Gårdsbutiken är värd omvägen.',
    order: 'Den lagrade. Be om att få smaka innan du köper.',
  },
  {
    slug: 'gronsakslandet',
    rating: 4,
    verdict: 'Grönsaker plockade samma morgon. Man märker skillnaden direkt.',
    order: 'Vad som än ligger överst i lådan. Det är säsongen.',
  },
  {
    slug: 'kafferosteriet-kalmar',
    rating: 4.5,
    verdict: 'Rostar ljust och står för det. Bästa bryggkaffet i Kalmar, punkt.',
    order: 'Bryggkaffe på månadens böna. Skippa mjölken.',
  },
  {
    slug: 'bryggeriet-i-skogen',
    rating: 4,
    verdict: 'Ölen är ärlig och lokalen är en lada. Båda delarna är poängen.',
    order: 'Ofiltrerade lagern, direkt från tanken.',
  },
  {
    slug: 'sjoboden',
    rating: 4.5,
    verdict: 'Skaldjur och havsutsikt. Kort säsong, desto större anledning att åka.',
    order: 'Skaldjursfatet och ett glas till. Boka i förväg.',
  },
];

export const verdictFor = (slug: string): Verdict | undefined =>
  VERDICTS.find((v) => v.slug === slug);

export interface CityGuide {
  city: string;
  /** Reads under the city name on the guide card. */
  strap: string;
  intro: string;
  /** A day in the town, told as four stops. */
  perfectDay: { time: string; what: string; where: string; slug?: string }[];
  image: string;
}

export const CITY_GUIDES: CityGuide[] = [
  {
    city: 'Växjö',
    strap: 'Bagerier, säsongsmat och en fikakultur som tar sig själv på allvar',
    intro:
      'Småland i miniatyr. Här finns det mesta inom gångavstånd, och de bästa ställena ligger sällan vid torget.',
    perfectDay: [
      { time: '08.00', what: 'Croissant och bryggkaffe', where: 'Lilla Verket', slug: 'lilla-verket' },
      { time: '11.30', what: 'Lunch på riktigt porslin', where: 'Top Notch Bakery', slug: 'top-notch-bakery' },
      { time: '15.00', what: 'Eftermiddagsfika', where: 'Kaféet i Linnéparken' },
      { time: '19.00', what: 'Säsongsmeny och ett glas', where: 'Kök 11', slug: 'kok-11' },
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8303ae1b-9d0f-45da-b556-99c68efa3216.jpg',
  },
  {
    city: 'Kalmar',
    strap: 'Kaffe, hav och en stadskärna som belönar den som går vilse',
    intro:
      'Kalmar gör kaffe bättre än de flesta svenska städer. Lägg till havet och du har en heldag.',
    perfectDay: [
      { time: '09.00', what: 'Bryggkaffe på månadens böna', where: 'Kafferosteriet Kalmar', slug: 'kafferosteriet-kalmar' },
      { time: '12.00', what: 'Lunch i Gamla stan', where: 'Kvarteret kring Larmtorget' },
      { time: '15.30', what: 'Promenad till slottet', where: 'Kalmar slott' },
      { time: '18.30', what: 'Middag med havsutsikt', where: 'Sjöboden', slug: 'sjoboden' },
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8b1aea01-7003-4539-af32-7d71f1ddddf8.jpg',
  },
  {
    city: 'Eksjö',
    strap: 'Trästaden med ett mejeri och ett bryggeri värda omvägen',
    intro:
      'Eksjö är litet nog att gå igenom på en timme och gott nog att stanna en helg. Åk för hantverket.',
    perfectDay: [
      { time: '10.00', what: 'Ostprovning i gårdsbutiken', where: 'Sörgårdens Mejeri', slug: 'sorgardens-mejeri' },
      { time: '13.00', what: 'Lunch i trästaden', where: 'Gamla stan' },
      { time: '16.00', what: 'Öl direkt från tanken', where: 'Bryggeriet i Skogen', slug: 'bryggeriet-i-skogen' },
      { time: '19.00', what: 'Middag på värdshuset', where: 'Eksjö stadshotell' },
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6e45c36e-5349-42b8-a31b-caf183124798.jpg',
  },
  {
    city: 'Vetlanda',
    strap: 'Konditoritraditioner som aldrig behövt moderniseras',
    intro: 'Kom hit för tårtan. Stanna för att ingen stressar dig ut ur lokalen.',
    perfectDay: [
      { time: '10.30', what: 'Budapesttårta och kaffe', where: 'Askelyckans Konditori', slug: 'askelyckan' },
      { time: '13.00', what: 'Smörgåstårta att ta med', where: 'Askelyckans Konditori', slug: 'askelyckan' },
      { time: '15.00', what: 'Gelato om det är sommar', where: 'Askelyckans Konditori', slug: 'askelyckan' },
      { time: '17.00', what: 'Promenad längs Emån', where: 'Emåns strandpromenad' },
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b2fc99a2-e202-40f5-8e13-d1a02111b749.jpg',
  },
];

export const guideFor = (city: string): CityGuide | undefined =>
  CITY_GUIDES.find((g) => g.city.toLowerCase() === city.toLowerCase());

export const placesIn = (city: string): Place[] =>
  PLACES.filter((p) => String(p.city).toLowerCase() === city.toLowerCase());

/** Our top-rated places across the whole region, best first. */
export const topRated = (limit = 4): { place: Place; verdict: Verdict }[] =>
  VERDICTS.slice()
    .sort((a, b) => b.rating - a.rating)
    .map((verdict) => ({ place: PLACES.find((p) => p.slug === verdict.slug)!, verdict }))
    .filter((row) => Boolean(row.place))
    .slice(0, limit);
